import React, {Fragment} from 'react'
import {
    Checkbox,
    Container,
    ContainerGutter,
    css,
    Text,
    TextTag,
    ThemeProvider,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'

import {ChevronDown} from '@volkswagen-onehub/icons-core'
import {Category} from '../types/category'

interface CommonProps {
    readonly 'data-testid'?: string
}

export interface Result {
    [id: string]: number
}

export interface MultiSelectAdvancedProps extends CommonProps {
    readonly name: string
    readonly options: Category[]
    readonly label?: string
    readonly labelId?: string
    readonly controlsId?: string
    readonly onClick?: React.MouseEventHandler<HTMLDivElement>
    readonly onChange?: (options: string | string[]) => void
    readonly onOptionClick?: (optionKey: string) => void
    readonly value?: number[]
    readonly defaultValue?: string[]
    readonly defaultSingleOption?: string
    readonly disabled?: boolean
    readonly singleOptionMode?: boolean
    readonly isFloating?: boolean
    readonly results?: Result
    readonly categorization?: boolean
}

const ANIMATION_FUNCTION = '0.3s cubic-bezier(0.14, 1.12, 0.67, 0.99)'
const OVERLAY_Z_INDEX = 1

const getCommonProps = (props: CommonProps): CommonProps => ({
    'data-testid': props['data-testid'],
})

const StyledOverlay = styled.div<{
    readonly singleOptionMode?: boolean
}>`
    position: absolute;
    z-index: ${OVERLAY_Z_INDEX};
    width: 100%;
    padding: ${({theme}) => theme.size.static250};
    background: ${({theme}) => theme.colors.background.primary};
    box-shadow: ${({theme}) => theme.colors.shadow.variant4};
    margin-top: ${({theme, singleOptionMode}) =>
        singleOptionMode
            ? theme.size.static200
            : `calc(${theme.size.static200} * 2)`};
    opacity: 0;
    transform: translateX(-100vw);

    &:dir(rtl) {
        transform: translateX(100vw);
    }

    &:focus-within {
        opacity: 1;
        transform: translateX(0);
    }

    top: ${({singleOptionMode}) => (singleOptionMode ? '100%' : 'initial')};
`

const StyledLabelWrapper = styled.div<{
    readonly internalDisabledProp?: boolean
}>`
    display: flex;
    align-items: center;
`

const StyledIcon = styled.span`
    position: relative;
    flex: 0 0 auto;
    overflow: visible;
    text-align: center;
    transition: ${({theme}) =>
        `transform ${ANIMATION_FUNCTION}, color ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};
    line-height: 0;
`

const StyledCountAndIconWrapper = styled.span`
    display: flex;
    align-items: center;
`

const StyledIconWrapper = styled.span`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${({theme}) => theme.size.static350};
`

const StyledInteractionWrapper = styled.div<{
    readonly internalDisabledProp?: boolean
}>`
    display: grid;
    grid-column-gap: ${({theme}) => theme.size.static150};
    grid-template-columns: auto max-content;
    align-items: center;
    width: 100%;
    min-height: ${({theme}) => theme.size.static350};
    pointer-events: ${({internalDisabledProp}) =>
        !internalDisabledProp ? 'all' : 'none'};
    color: ${({theme, internalDisabledProp}) =>
        theme.colors.content[internalDisabledProp ? 'disabled' : 'primary']};
`

const StyledCount = styled.span`
    box-sizing: content-box;
    margin-inline-start: ${({theme}) => theme.size.static200};
    min-width: 1em;
    line-height: 1em;
    padding: ${({theme}) => theme.size.static100};
    border-radius: ${({theme}) => theme.size.borderRadius.badge};
    font-size: ${({theme}) => theme.fonts.label[100].fontSize};
    text-align: center;
    color: ${({theme}) => theme.colors.content.inverted};
    background: ${({theme}) => theme.colors.signal.neutral};
    height: fit-content;
    width: fit-content;
`

const StyledMultiSelectAdvanced = styled.fieldset<{
    disabled?: boolean
}>`
    margin: 0;
    padding: ${({theme}) => theme.size.static200}
        ${({theme}) => theme.size.static250};
    opacity: unset;
    position: relative;
    box-shadow: 0 0 0 ${({theme}) => theme.size.border.default}
        ${({theme}) => theme.colors.border.default};
    border: none;
    border-radius: ${({theme}) => theme.size.borderRadius.select};
    cursor: ${({disabled}) => (!disabled ? 'pointer' : 'not-allowed')};

    ${({disabled, theme}) =>
        !disabled &&
        css`
            :focus-within,
            :hover {
                ${StyledIcon} {
                    color: ${theme.colors.button.tertiary.active};
                }

                ${StyledLabelWrapper} {
                    color: ${theme.colors.content.hover};
                }
            }

            :focus-within {
                ${StyledIcon} {
                    transform: rotateX(180deg);
                }
            }
        `}
`

const StyledLegend = styled.legend`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`

const StyledResults = styled.span`
    color: ${({theme}) => theme.colors.content.secondary};
    margin-left: ${({theme}) => theme.size.static150};
`

const StyledName = styled(Text as any)`
    margin-bottom: ${({theme}) => theme.size.static150};
`

/* The onMouseDown preventDefault is needed to ensure that the focus will
	not be removed from the checkbox when clicked */
const handleCheckboxOnMouseDown = (
    event: React.MouseEvent<HTMLLabelElement, MouseEvent>
) => {
    event.preventDefault()
    event.currentTarget.focus()
}

const handleOverlayOnMouseDown = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
) => {
    event.preventDefault()
}

const handleNewValue = (value: string[], optionKey: string) => {
    const newValue = [...value]
    if (value.includes(optionKey)) {
        newValue.splice(value.indexOf(optionKey), 1)
    } else {
        newValue.push(optionKey)
    }

    return newValue
}

const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (event) => {
    /* The onMouseDown preventDefault is needed to ensure that the focus will not be removed from the InteractionWrapper when clicked */
    event.preventDefault()
}

interface MultiSelectState {
    internalValue: string[]
}

export const MultiSelectAdvanced: React.FunctionComponent<
    MultiSelectAdvancedProps
> = (props) => {
    const firstOption = React.useRef<HTMLInputElement>(null)
    const multiSelect = React.useRef<HTMLFieldSetElement>(null)
    const [internalValue, setInternalValue] = React.useState<
        MultiSelectState['internalValue']
    >(props.defaultValue || [])

    const {
        defaultValue,
        disabled,
        label,
        onChange,
        onOptionClick,
        options,
        results,
        value,
        labelId,
        controlsId,
    } = props
    const accessibleLabelId = labelId ?? `${props.name}-filter-label`
    const optionsId = controlsId ?? `${props.name}-filter-options`

    const handleClick = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement>): void => {
            const {onClick} = props

            if (firstOption?.current && multiSelect?.current) {
                const elementWithFocus =
                    multiSelect.current.querySelector(':focus')

                if (elementWithFocus) {
                    ;(elementWithFocus as HTMLElement).blur()

                    return
                }

                firstOption.current.focus()
            }

            if (onClick) {
                onClick(event)
            }
        },
        [props]
    )

    const commonProps = getCommonProps(props)

    // Group categories by category name
    const groupedCategories = options.reduce((acc, entry) => {
        entry.categories.forEach(({id, name}) => {
            // eslint-disable-next-line security/detect-object-injection
            if (!acc[name]) {
                // eslint-disable-next-line security/detect-object-injection
                acc[name] = []
            }
            // eslint-disable-next-line security/detect-object-injection
            acc[name].push({id, specialization: entry.name})
        })

        return acc
    }, {} as Record<string, {id: number; specialization: string}[]>)

    const optionsElement = Object.entries(groupedCategories).map(
        ([categoryName, specializations]) => (
            <Fragment key={categoryName}>
                <StyledName appearance={TokenTextAppearance.copy200} bold>
                    {categoryName}
                </StyledName>
                {specializations.map(({id, specialization}) => (
                    <div key={id}>
                        <Checkbox
                            checked={value && value.includes(id)}
                            defaultChecked={
                                defaultValue &&
                                defaultValue.includes(id.toString())
                            }
                            disabled={disabled}
                            key={id}
                            label={
                                // eslint-disable-next-line security/detect-object-injection
                                results && results[id] ? (
                                    <div>
                                        {specialization}
                                        <StyledResults>
                                            {/* eslint-disable-next-line security/detect-object-injection */}
                                            ({results[id]})
                                        </StyledResults>
                                    </div>
                                ) : (
                                    specialization
                                )
                            }
                            onChange={() => {
                                if (value) {
                                    return
                                }

                                const newInternalValue = handleNewValue(
                                    internalValue,
                                    id.toString()
                                )

                                setInternalValue(newInternalValue)
                                if (onChange) {
                                    onChange(newInternalValue)
                                }
                            }}
                            onClick={() => {
                                if (onOptionClick) {
                                    onOptionClick(id.toString())
                                }
                            }}
                            onLabelMouseDown={handleCheckboxOnMouseDown}
                            name={specialization}
                            ref={firstOption}
                            value={id.toString()}
                        />
                    </div>
                ))}
            </Fragment>
        )
    )

    const count = value ? value.length : internalValue.length

    return (
        <StyledMultiSelectAdvanced
            aria-labelledby={accessibleLabelId}
            disabled={disabled}
            ref={multiSelect}
            {...commonProps}
        >
            <StyledLegend id={accessibleLabelId}>{label}</StyledLegend>

            <StyledInteractionWrapper
                aria-hidden
                aria-controls={optionsId}
                onClick={handleClick}
                onMouseDown={handleMouseDown}
            >
                <StyledLabelWrapper>
                    <Text
                        appearance={TokenTextAppearance.copy200}
                        color={TokenTextColor.inherit}
                        staticSize
                        tag={TextTag.span}
                    >
                        {label}
                    </Text>

                    {count > 0 && (
                        <ThemeProvider theme="main">
                            <StyledCount>{count}</StyledCount>
                        </ThemeProvider>
                    )}
                </StyledLabelWrapper>

                <StyledCountAndIconWrapper>
                    <StyledIconWrapper>
                        <StyledIcon>
                            <ChevronDown aria-hidden variant="default" />
                        </StyledIcon>
                    </StyledIconWrapper>
                </StyledCountAndIconWrapper>
            </StyledInteractionWrapper>

            <ThemeProvider theme="main">
                <StyledOverlay
                    aria-labelledby={accessibleLabelId}
                    id={optionsId}
                    onMouseDown={handleOverlayOnMouseDown}
                    role="group"
                >
                    <Container gutter={ContainerGutter.static200} wrap="always">
                        {optionsElement}
                    </Container>
                </StyledOverlay>
            </ThemeProvider>
        </StyledMultiSelectAdvanced>
    )
}
